import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import { useWebinarT } from '@/hooks/useWebinarT';
import { useWebinarLng } from '@/contexts/WebinarLanguageContext';
import ChatView from '@/components/pages/live/ChatView';
import FooterBar from '@/components/pages/live/FootBar';
import { LiveKitRoom } from '@/components/pages/live/LiveKitRoom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  fetchMyPublicQuizSubmission,
  fetchPublicOffer,
  fetchPublicPlayback,
  fetchPublicQuiz,
  fetchPublicResource,
  fetchPublicResourceState,
  fetchPublicSession,
  fetchPublicSlide,
  fetchPublicSurvey,
  fetchPresenceStatus,
} from '@/features/live/liveThunks';
import type { WebinarResource } from '@/features/webinar/types';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import { useChannelName } from '@/routes/AblyAuth';
import { useChannel } from 'ably/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { clearPublicResource, clearPublicSlide } from '@/features/live/liveSlice';
import { formatPlannedLiveDate } from '@/lib/formatPlannedDate';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  MediaDeviceTester,
  type MediaDeviceSelection,
} from '@/components/pages/live/admin/MediaDeviceTester';
import { PretestDevicesProvider } from '@/contexts/PretestDevicesContext';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import SplashScreen from '@/components/common/SplashScreen';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Field } from '@headlessui/react';
import { REGEXP_ONLY_DIGITS } from 'input-otp';
import { validatePublicSessionCode } from '@/features/webinar/webinarThunks';
import { parsePresenceUpdateLiveCount } from '@/lib/presenceUpdate';
import CamLayoutProvider from '@/features/live/CamLayoutProvider';
import {
  SPEAKER_STATE_HEARTBEAT_MS,
  canRestoreStage,
  clearSpeakerSessionState,
  getSpeakerStateStore,
  readSpeakerSessionState,
  speakerSessionStateKey,
  touchSpeakerSessionState,
  writeSpeakerSessionState,
} from '@/lib/speakerSessionState';

type SpeakerLivePageProps = {
  role?: string;
  userId?: string;
};

export default function SpeakerLivePage({ userId }: SpeakerLivePageProps) {
  const { t, i18n } = useWebinarT('common');
  const lng = useWebinarLng() ?? i18n.language;
  const { t: tw } = useWebinarT('webinars');
  const dispatch = useAppDispatch();
  const { webinarId: webinarIdParam, sessionIdentifier: sessionIdentifierParam } = useParams<{
    webinarId: string;
    sessionIdentifier: string;
  }>();

  // Un rechargement ne remet pas l'invité au code, à la préparation ni à l'attente d'invitation :
  // l'état de la session est relu une fois au montage, puis réécrit à chaque changement.
  const parsedWebinarId = webinarIdParam ? Number.parseInt(webinarIdParam, 10) : NaN;
  const speakerStateKey = speakerSessionStateKey(
    Number.isFinite(parsedWebinarId) ? parsedWebinarId : null,
    sessionIdentifierParam,
  );
  const [restoredSpeakerState] = useState(() =>
    readSpeakerSessionState(getSpeakerStateStore(), speakerStateKey),
  );

  const [showJoinRequestModal, setShowJoinRequestModal] = useState(false);
  const [joinRequestSpeakerId, setJoinRequestSpeakerId] = useState<number | null>(null);
  const [speakerTracksEnabled, setSpeakerTracksEnabled] = useState(() =>
    canRestoreStage(restoredSpeakerState, Date.now()),
  );
  const [speakerParticipantDisplayName, setSpeakerParticipantDisplayName] = useState<
    string | undefined
  >(undefined);
  const [pretestDevices, setPretestDevices] = useState<MediaDeviceSelection | null>(null);
  const publicResourceRef = useRef<WebinarResource | null>(null);

  const { roleToken } = useAppSelector((s) => s.live);
  const publicResourceStates = useAppSelector((s: RootState) => s.live.publicResourceStates);
  const publicResource = useAppSelector((s: RootState) => s.live.publicResource);
  const publicSlide = useAppSelector((s: RootState) => s.live.publicSlide);

  useEffect(() => {
    publicResourceRef.current = publicResource;
  }, [publicResource]);

  const channelName = useChannelName();
  const channel = useChannel(channelName || '');

  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const webinarId = webinarIdParam ? parseInt(webinarIdParam, 10) : null;
  const sessionIdentifier = sessionIdentifierParam;

  const { publicSession } = useAppSelector((s: RootState) => s.live);
  const presenceStatus = useAppSelector((s: RootState) => s.live.presenceStatus);
  const roleTokenError = useAppSelector((s: RootState) => s.live.roleTokenError);
  const playbackError = useAppSelector((s: RootState) => s.live.playbackError);
  const publicSessionError = useAppSelector((s: RootState) => s.live.publicSessionError);
  const [isError, setIsError] = useState<string | null>(null);
  const [validadeCode, setValidadeCode] = useState(restoredSpeakerState.codeValidated);
  const [webinarConfigOverride, setWebinarConfigOverride] = useState<{
    displayParticipantsMinThreshold?: number;
    displayParticipantsCountEnabled?: boolean;
    chatMode?: 'PUBLIC' | 'PRIVATE' | 'DISABLED';
  } | null>(null);
  const [liveParticipantCount, setLiveParticipantCount] = useState(0);

  const chatMode = webinarConfigOverride?.chatMode ?? publicSession?.session?.chatMode ?? 'PUBLIC';
  const isSessionExpired = (err: string | null | undefined) =>
    !!err &&
    (err.includes('401') ||
      err.toLowerCase().includes('session expired') ||
      err.includes('SESSION_EXPIRED'));

  useEffect(() => {
    if (!webinarId || !sessionIdentifier || !token || !publicResourceStates?.length) {
      return;
    }

    const liveOffer = publicResourceStates.find(
      (r) => r.resourceType === 'OFFER_TEMPLATE' && r.live,
    );

    if (liveOffer) {
      dispatch(
        fetchPublicOffer({
          webinarId,
          offerId: liveOffer.resourceId,
          authorization: token,
        }),
      );
    }

    const liveSurvey = publicResourceStates.find(
      (r) => r.resourceType === 'SURVEY_TEMPLATE' && r.live,
    );

    if (liveSurvey) {
      dispatch(
        fetchPublicSurvey({
          webinarId,
          surveyId: liveSurvey.resourceId,
          authorization: token,
        }),
      );
    }

    const liveQuiz = publicResourceStates.find((r) => r.resourceType === 'QUIZ_TEMPLATE' && r.live);

    if (liveQuiz) {
      dispatch(
        fetchPublicQuiz({
          webinarId,
          quizId: liveQuiz.resourceId,
          authorization: token,
        }),
      );

      dispatch(
        fetchMyPublicQuizSubmission({
          webinarId,
          templateId: liveQuiz.resourceId,
          authorization: token,
        }),
      );
    }

    const liveVideo = publicResourceStates.find(
      (r) => r.resourceType === 'WEBINAR_RESOURCE' && r.live,
    );

    if (liveVideo) {
      dispatch(
        fetchPublicResource({
          webinarId,
          resourceId: liveVideo.resourceId,
          authorization: token,
        }),
      );
    }

    const liveSlide = publicResourceStates.find(
      (r) => r.resourceType === 'WEBINAR_SLIDE' && r.live,
    );

    if (liveSlide) {
      dispatch(
        fetchPublicSlide({
          webinarId,
          slideId: liveSlide.resourceId,
          authorization: token,
        }),
      );
    }
  }, [webinarId, sessionIdentifier, token, publicResourceStates, dispatch]);

  useEffect(() => {
    if (webinarId && sessionIdentifier && token) {
      dispatch(
        fetchPublicPlayback({
          webinarId,
          sessionIdentifier,
          authorization: token,
        }),
      );
      dispatch(
        fetchPublicSession({
          webinarId,
          sessionIdentifier,
        }),
      );
      dispatch(
        fetchPublicResourceState({
          webinarId,
          sessionIdentifier,
          authorization: token,
        }),
      );
    }
  }, [webinarId, sessionIdentifier, token, dispatch]);

  useEffect(() => {
    if (!webinarId || !sessionIdentifier || !token) {
      return;
    }
    void dispatch(
      fetchPresenceStatus({
        webinarId,
        sessionIdentifier,
        authorization: token,
      }),
    );
  }, [webinarId, sessionIdentifier, token, dispatch]);

  useEffect(() => {
    if (presenceStatus?.liveCount !== undefined) {
      setLiveParticipantCount(presenceStatus.liveCount);
    }
  }, [presenceStatus?.liveCount]);

  const visualIdentity = useAppSelector((s: RootState) => s.live.publicSession?.visualIdentity);
  const formattedPlannedDate = useMemo(
    () => formatPlannedLiveDate(publicSession?.session.utcPlannedDate, lng),
    [publicSession?.session.utcPlannedDate, lng],
  );

  const [showCodeModal, setShowCodeModal] = useState(!restoredSpeakerState.codeValidated);
  const [showSetupModal, setShowSetupModal] = useState(!restoredSpeakerState.setupDone);
  const [cameraEnabled, setCameraEnabled] = useState(restoredSpeakerState.cameraEnabled);
  const [microphoneEnabled, setMicrophoneEnabled] = useState(
    restoredSpeakerState.microphoneEnabled,
  );
  const [checkedSetup, setCheckedSetup] = useState(false);

  const sessionFinished = publicSession?.session.status === 'FINISHED';

  useEffect(() => {
    if (sessionFinished) return;
    writeSpeakerSessionState(getSpeakerStateStore(), speakerStateKey, {
      codeValidated: validadeCode,
      setupDone: !showSetupModal,
      onStage: speakerTracksEnabled,
      cameraEnabled,
      microphoneEnabled,
      savedAtMs: Date.now(),
    });
  }, [
    sessionFinished,
    speakerStateKey,
    validadeCode,
    showSetupModal,
    speakerTracksEnabled,
    cameraEnabled,
    microphoneEnabled,
  ]);

  // Tant que la page est au premier plan, l'horodatage suit : un invité qui reste sur scène sans
  // rien toucher garde un état frais, un onglet endormi cesse d'en produire.
  useEffect(() => {
    if (sessionFinished) return;
    const touch = () => {
      if (document.visibilityState !== 'visible') return;
      touchSpeakerSessionState(getSpeakerStateStore(), speakerStateKey, Date.now());
    };
    const intervalId = window.setInterval(touch, SPEAKER_STATE_HEARTBEAT_MS);
    document.addEventListener('visibilitychange', touch);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', touch);
    };
  }, [sessionFinished, speakerStateKey]);

  useEffect(() => {
    if (sessionFinished) clearSpeakerSessionState(getSpeakerStateStore(), speakerStateKey);
  }, [sessionFinished, speakerStateKey]);
  const [inputCode, setInputCode] = useState('');
  const handleRejectJoinRequest = () => {
    setShowJoinRequestModal(false);
    if (!channelName || !userId || joinRequestSpeakerId == null) {
      return;
    }
    // Publication Ably directe : le token d'invitation speaker a la capability `publish` sur le
    // canal, alors que l'endpoint public /events/trigger refuse ce rôle (403).
    channel.channel
      .publish('live-event', {
        eventType: 'SPEAKER_JOIN_DECLINED',
        resourceType: 'SPEAKER',
        resourceId: joinRequestSpeakerId,
        payloadJson: JSON.stringify({ speakerUserId: userId }),
      })
      .catch((err: unknown) => {
        console.error('Ably SPEAKER_JOIN_DECLINED publish failed:', err);
      });
  };

  const handleAcceptJoinRequest = async () => {
    setSpeakerTracksEnabled(true);
    setShowJoinRequestModal(false);

    console.log('Speaker tracks enabled, identity:', roleToken?.identity || 'speaker-1');
  };

  const handleValidateCode = async () => {
    if (!webinarId || !sessionIdentifier || !token) {
      return;
    }
    const result = await dispatch(
      validatePublicSessionCode({
        webinarId: webinarId,
        sessionIdentifier: sessionIdentifier,
        token: token,
        code: inputCode,
      }),
    );
    if (validatePublicSessionCode.fulfilled.match(result)) {
      setShowCodeModal(false);
      setValidadeCode(true);
      // Le PDF n'est monté qu'après la validation du code : on resynchronise l'état des
      // ressources live pour repartir sur la slide/page réellement diffusée (et non celle
      // figée au chargement de la page, avant les changements faits pendant la saisie du code).
      void dispatch(
        fetchPublicResourceState({
          webinarId,
          sessionIdentifier,
          authorization: token,
        }),
      );
    } else {
      setIsError(tw('live.accessCode.invalid'));
      setValidadeCode(false);
    }
  };

  useEffect(() => {
    if (!channelName || !userId || !channel.channel) {
      console.log('useEffect conditions not met:', {
        channelName,
        userId,
        hasChannel: !!channel.channel,
      });
      return;
    }

    type LiveEventData = {
      eventType?: string;
      resourceType?: string;
      resourceId?: unknown;
      payloadJson?: unknown;
    };

    const parsePayload = (payloadJson: unknown): Record<string, unknown> => {
      if (!payloadJson) return {};
      if (typeof payloadJson === 'string') {
        try {
          const parsed = JSON.parse(payloadJson);
          return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
        } catch {
          return {};
        }
      }
      return payloadJson && typeof payloadJson === 'object'
        ? (payloadJson as Record<string, unknown>)
        : {};
    };

    const handleLiveEventData = (data: LiveEventData) => {
      if (!data || typeof data !== 'object') return;
      if (data.resourceType !== 'SPEAKER') return;

      const payload = parsePayload(data.payloadJson);

      console.log('payload', payload);
      const speakerUserId =
        (payload.speakerUserId as string | number | undefined) ??
        (payload.speakerId as string | number | undefined) ??
        (payload.userId as string | number | undefined);
      payload.displayName as string | undefined;

      if (data.eventType === 'SPEAKER_JOIN_REQUEST') {
        console.log('Comparaison:', {
          speakerUserId,
          userId,
          speakerUserIdString: String(speakerUserId),
          userIdString: String(userId),
          match: String(speakerUserId) === String(userId),
        });

        if (speakerUserId != null && String(speakerUserId) === String(userId)) {
          console.log('Match trouvé, ouverture de la modal');
          const dn = payload.displayName;
          if (typeof dn === 'string' && dn.trim()) {
            setSpeakerParticipantDisplayName(dn.trim());
          }
          setJoinRequestSpeakerId(typeof data.resourceId === 'number' ? data.resourceId : null);
          setShowJoinRequestModal(true);
        }
      }

      if (data.eventType === 'SPEAKER_REMOVE_REQUEST') {
        if (speakerUserId != null && String(speakerUserId) === String(userId)) {
          setSpeakerTracksEnabled(false);
          setSpeakerParticipantDisplayName(undefined);
        }
      }
    };

    const handleSpeakerEvents = (message: any) => {
      if (!message) return;

      if (message.name === 'live-event') {
        handleLiveEventData(message.data as LiveEventData);
        return;
      }

      if (message.name === 'batch-events' && Array.isArray(message.data)) {
        for (const item of message.data) {
          if (item && typeof item === 'object' && (item as any).name === 'live-event') {
            handleLiveEventData((item as any).data as LiveEventData);
          }
        }
      }
    };

    channel.channel.subscribe('live-event', handleSpeakerEvents);
    channel.channel.subscribe('batch-events', handleSpeakerEvents);

    return () => {
      channel.channel.unsubscribe('live-event', handleSpeakerEvents);
      channel.channel.unsubscribe('batch-events', handleSpeakerEvents);
    };
  }, [channelName, userId, channel]);

  useChannel(channelName!, (message) => {
    if (!message) return;

    const normalizedMessages =
      message.name === 'batch-events' && Array.isArray(message.data)
        ? message.data.filter(
            (event): event is { name: string; data: unknown } =>
              !!event &&
              typeof event === 'object' &&
              typeof (event as { name?: unknown }).name === 'string',
          )
        : [{ name: message.name, data: message.data }];

    for (const currentMessage of normalizedMessages) {
      if (currentMessage.name === 'presence-update') {
        const live = parsePresenceUpdateLiveCount(currentMessage.data);
        if (live !== null) {
          setLiveParticipantCount(live);
        }
        continue;
      }
      if (currentMessage.name !== 'live-event') continue;
      const configData = currentMessage.data as Record<string, unknown> | null | undefined;
      if (
        configData &&
        typeof configData === 'object' &&
        configData.eventType === 'WEBINAR_CONFIG_UPDATED' &&
        configData.resourceType === 'WEBINAR' &&
        configData.payloadJson
      ) {
        try {
          const payload =
            typeof configData.payloadJson === 'string'
              ? JSON.parse(configData.payloadJson)
              : configData.payloadJson;
          if (payload && typeof payload === 'object') {
            const p = payload as {
              displayParticipantsMinThreshold?: number;
              displayParticipantsCountEnabled?: boolean;
              chatMode?: 'PUBLIC' | 'PRIVATE' | 'DISABLED';
            };
            setWebinarConfigOverride((prev) => ({
              ...(prev ?? {}),
              ...(p.displayParticipantsMinThreshold !== undefined
                ? { displayParticipantsMinThreshold: p.displayParticipantsMinThreshold }
                : {}),
              ...(p.displayParticipantsCountEnabled !== undefined
                ? { displayParticipantsCountEnabled: p.displayParticipantsCountEnabled }
                : {}),
              ...(p.chatMode !== undefined ? { chatMode: p.chatMode } : {}),
            }));
          }
        } catch {
          // ignore parse error
        }
      }

      if (
        configData &&
        typeof configData === 'object' &&
        (configData.eventType === 'SESSION_UPDATED' || configData.eventType === 'SESSION_CLOSE') &&
        configData.resourceType === 'SESSION' &&
        webinarId &&
        sessionIdentifier
      ) {
        void dispatch(fetchPublicSession({ webinarId, sessionIdentifier }));
      }

      if (configData && typeof configData === 'object') {
        if (configData.resourceType === 'WEBINAR_RESOURCE') {
          if (configData.eventType === 'RESOURCE_SHOW' && webinarId && token) {
            dispatch(
              fetchPublicResource({
                webinarId,
                resourceId: configData.resourceId as number,
                authorization: token,
              }),
            );
          } else if (configData.eventType === 'RESOURCE_HIDE') {
            dispatch(clearPublicResource());
          }
        }

        if (configData.resourceType === 'WEBINAR_SLIDE') {
          if (configData.eventType === 'RESOURCE_SHOW' && webinarId && token) {
            dispatch(
              fetchPublicSlide({
                webinarId,
                slideId: configData.resourceId as number,
                authorization: token,
              }),
            );
          } else if (configData.eventType === 'RESOURCE_HIDE') {
            const hiddenId = configData.resourceId as number | undefined;
            if (!hiddenId || publicSlide?.id === hiddenId) {
              dispatch(clearPublicSlide());
            }
          }
        }
      }
    }
  });

  if (
    isSessionExpired(roleTokenError) ||
    isSessionExpired(playbackError) ||
    isSessionExpired(publicSessionError)
  ) {
    return <SplashScreen label={tw('live.sessionExpired')} fullscreen />;
  }
  const isReady =
    !!webinarId && !!sessionIdentifier && !!token && !!publicSession && !!visualIdentity;

  if (!isReady) {
    return <SplashScreen label={tw('live.loading')} />;
  }
  if (publicSession?.session.status === 'FINISHED') {
    return <SplashScreen label={tw('live.sessionFinished')} fullscreen />;
  }

  return (
    <>
      {publicSession?.session.accessCodeEnabled && (
        <Dialog open={showCodeModal} onOpenChange={setShowCodeModal}>
          <DialogContent
            className="max-w-3xl"
            onOpenAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
            hideCloseButton
          >
            <DialogHeader className="text-left">
              <DialogTitle>{tw('live.accessCode.title')}</DialogTitle>
            </DialogHeader>
            <DialogDescription>{tw('live.accessCode.description')}</DialogDescription>
            <Field className="w-fit">
              <InputOTP
                className="w-full"
                id="digits-only"
                maxLength={6}
                pattern={REGEXP_ONLY_DIGITS}
                onChange={(value) => setInputCode(value)}
              >
                <InputOTPGroup>
                  <InputOTPSlot autoFocus index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </Field>
            {isError && <DialogDescription className="text-red-500">{isError}</DialogDescription>}
            <DialogFooter>
              <Button onClick={handleValidateCode} disabled={inputCode.length !== 6}>
                {tw('live.accessCode.enter')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {/* Sortie uniquement par « Entrer dans la salle » : la croix et le clic hors modale
          contournaient la vérification du matériel. */}
      <Dialog
        open={(!publicSession?.session.accessCodeEnabled || !showCodeModal) && showSetupModal}
      >
        {/* Seul le test d'appareils défile : la case et le bouton restent visibles sur petit écran. */}
        <DialogContent
          className="max-w-3xl flex flex-col overflow-hidden max-h-[90svh] gap-3 p-4 md:gap-4 md:p-6"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          hideCloseButton
        >
          <DialogHeader className="text-left">
            <DialogTitle className="text-2xl pb-0 md:text-3xl md:pb-4">
              {tw('live.setupCheck.title')}
            </DialogTitle>
            <DialogDescription className="hidden md:block">
              {tw('live.setupCheck.description')}
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 overflow-y-auto -mx-1 px-1">
            <MediaDeviceTester
              theme={visualIdentity?.theme === 'DARK' ? 'DARK' : 'LIGHT'}
              cameraEnabled={cameraEnabled}
              microphoneEnabled={microphoneEnabled}
              setCameraEnabled={setCameraEnabled}
              setMicrophoneEnabled={setMicrophoneEnabled}
              onDevicesChange={setPretestDevices}
            />
          </div>
          <div className="flex items-center gap-2 md:pt-2">
            <Checkbox
              id="checked-setup-speaker"
              checked={checkedSetup}
              onCheckedChange={(checked) => setCheckedSetup(!!checked)}
            />
            <Label htmlFor="checked-setup-speaker">{tw('live.setupCheck.confirmed')}</Label>
          </div>

          <DialogFooter className="flex justify-end md:pt-4">
            <Button
              onClick={() => setShowSetupModal(false)}
              disabled={!checkedSetup}
              className="ml-auto"
            >
              {tw('live.setupCheck.enterRoom')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {(!publicSession?.session.accessCodeEnabled || validadeCode) && (
        <>
          {/* h-svh et non h-screen : sur mobile h-screen compte les barres du navigateur et fait déborder le chat. */}
          <div
            className="h-svh max-w-[2400px] mx-auto w-full flex flex-col lg:flex-row lg:justify-center lg:gap-6 overflow-hidden min-h-0 compact-landscape:flex-row compact-landscape:justify-center compact-landscape:gap-4 compact-landscape:pl-[env(safe-area-inset-left)] compact-landscape:pr-[env(safe-area-inset-right)]"
            style={{ backgroundColor: visualIdentity?.backgroundColor }}
          >
            <div className="flex shrink-0 flex-col items-center gap-4 lg:flex-1 lg:shrink lg:min-w-0 lg:min-h-0 lg:pl-6 lg:py-6 compact-landscape:flex-2 compact-landscape:shrink compact-landscape:min-w-0 compact-landscape:min-h-0 compact-landscape:h-full">
              <p
                className={`w-full px-4 pt-2 text-center text-xs lg:hidden ${visualIdentity?.theme === 'DARK' ? 'text-white/70' : 'text-secondary'}`}
                style={{ fontFamily: visualIdentity?.fontFamily || 'Poppins' }}
              >
                {tw('live.keepPageOpen')}
              </p>
              <div className="w-full aspect-video relative lg:aspect-auto lg:flex-1 lg:min-h-0 lg:h-full lg:flex lg:items-stretch lg:justify-stretch compact-landscape:aspect-auto compact-landscape:flex-1 compact-landscape:min-h-0 compact-landscape:h-full compact-landscape:flex compact-landscape:items-stretch compact-landscape:justify-stretch">
                <CamLayoutProvider
                  workspaceId={publicSession?.session.workspaceId}
                  webinarId={webinarId ?? undefined}
                  sessionId={publicSession?.session.id}
                  role="speaker"
                >
                  <PretestDevicesProvider value={pretestDevices} setSelection={setPretestDevices}>
                    <LiveKitRoom
                      roomName={roleToken?.roomName || ''}
                      token={roleToken?.token || ''}
                      userId={roleToken?.identity ?? ''}
                      isSpeaker
                      speakerTracksEnabled={speakerTracksEnabled}
                      participantDisplayName={speakerParticipantDisplayName}
                      cameraEnabled={cameraEnabled}
                      microphoneEnabled={microphoneEnabled}
                      videoDeviceId={pretestDevices?.cameraId}
                      audioDeviceId={pretestDevices?.microphoneId}
                      onLocalCameraEnabledChange={setCameraEnabled}
                      onLocalMicrophoneEnabledChange={setMicrophoneEnabled}
                    />
                  </PretestDevicesProvider>
                </CamLayoutProvider>
              </div>

              {channelName && (
                <div className="w-full hidden lg:block">
                  <FooterBar
                    isAttendee
                    liveParticipantCount={liveParticipantCount}
                    liveStartAt={publicSession?.session.startedAt ?? undefined}
                    visualIdentity={visualIdentity as WorkspaceVisualIdentity}
                    formattedPlannedDate={formattedPlannedDate}
                    displayParticipantsMinThreshold={
                      webinarConfigOverride?.displayParticipantsMinThreshold ?? 0
                    }
                    displayParticipantsCountEnabled={
                      webinarConfigOverride?.displayParticipantsCountEnabled ?? true
                    }
                  />
                </div>
              )}
            </div>

            <div className="block flex-1 min-h-0 lg:min-w-[280px] lg:max-w-[456px] lg:flex-1 lg:pr-6 lg:h-svh compact-landscape:min-w-[280px] compact-landscape:max-w-[456px] compact-landscape:flex-1 compact-landscape:h-full">
              <div className="h-full flex flex-col min-h-0 relative">
                {chatMode === 'DISABLED' ? (
                  <div className="h-full flex flex-col items-center justify-center p-6 text-center">
                    <p
                      className={
                        visualIdentity?.theme === 'DARK'
                          ? 'text-white/70 text-sm'
                          : 'text-secondary text-sm'
                      }
                      style={{
                        fontFamily: visualIdentity?.fontFamily || 'Poppins',
                      }}
                    >
                      {t('liveSettings.chatDisabledMessage')}
                    </p>
                  </div>
                ) : (
                  <ChatView
                    workspaceId={publicSession?.session.workspaceId}
                    webinarId={publicSession?.session.webinarId}
                    sessionId={publicSession?.session.id}
                    chatMode={chatMode}
                    channelName={channelName || ''}
                    userId={userId?.toString() ?? ''}
                    userName="Speaker"
                    isSpeaker
                    liveParticipantCountSync={liveParticipantCount}
                    visualIdentity={visualIdentity as WorkspaceVisualIdentity}
                    speakerAuthorization={token ?? undefined}
                  />
                )}
              </div>
            </div>
          </div>
          <AlertDialog open={showJoinRequestModal} onOpenChange={setShowJoinRequestModal}>
            <AlertDialogContent
              className="max-w-3xl"
              style={{
                backgroundColor: visualIdentity?.backgroundColor || '#FFFFFF',
              }}
            >
              <AlertDialogHeader>
                <AlertDialogTitle
                  style={{
                    color: visualIdentity?.theme === 'DARK' ? '#FFFFFF' : '#000000',
                  }}
                >
                  {tw('live.joinRequest.title')}
                </AlertDialogTitle>
                <AlertDialogDescription
                  style={{
                    color: visualIdentity?.theme === 'DARK' ? '#FFFFFF' : '#000000',
                  }}
                >
                  {tw('live.joinRequest.description')}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel
                  onClick={handleRejectJoinRequest}
                  style={{
                    backgroundColor: visualIdentity?.secondaryBackgroundColor || undefined,
                  }}
                >
                  {tw('live.joinRequest.decline')}
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleAcceptJoinRequest}
                  style={{
                    backgroundColor: visualIdentity?.contrastColor || undefined,
                  }}
                >
                  {tw('live.joinRequest.accept')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </>
  );
}
